<template>
  <section class="section advantages">
    <h2>
      {{
        $t("{brand} advantages", {
          brand: brand,
        })
      }}
    </h2>

    <div class="advantages-container">
      <div class="advantages-select-tab">
        <SelectTab
          :options="SELECT_TAB_OPTIONS"
          :value="currentUser"
          @select="changeUser"
        >
          <template #option="slotProps">
            {{ $t(slotProps.option.value) }}
          </template>
        </SelectTab>
      </div>

      <div
        class="advantages-tab advantages-tab-user"
        :class="{ 'advantages-tab_hidden': currentUser !== User.members }"
      >
        <h3>{{ $t("For Members") }}</h3>
        <div class="advantages-content">
          <div class="advantages-images">
            <ScrollSlider
              :items="<IScrollSliderItem[]>$tm(`advantages_section.${User.members}.slider.images`)"
            >
              <template #scroll-slider-item="{ item }">
                <PictureComponent
                  :modern-format-image="{
                    'desktop-path': $rt(
                      item.item.image['modern-format-image']['desktop-path']
                    ),
                    'desktop-path-retina': $rt(
                      item.item.image['modern-format-image'][
                        'desktop-path-retina'
                      ]
                    ),
                  }"
                  :fallback-format-image="{
                    'desktop-path': $rt(
                      item.item.image['fallback-format-image']['desktop-path']
                    ),
                    'desktop-path-retina': $rt(
                      item.item.image['fallback-format-image'][
                        'desktop-path-retina'
                      ]
                    ),
                  }"
                  loading="lazy"
                />
              </template>
            </ScrollSlider>

            <PictureComponent
              :modern-format-image="{
                'desktop-path': '/image/advantagesSection/user.webp',
                'desktop-path-retina': '/image/advantagesSection/user@2x.webp',
              }"
              :fallback-format-image="{
                'desktop-path': '/image/advantagesSection/user.png',
                'desktop-path-retina': '/image/advantagesSection/user@2x.png',
              }"
              loading="lazy"
            />
          </div>
          <div class="advantages-text">
            <div class="tab-title">
              {{ $t(`advantages_section.${User.members}.title`) }}
            </div>
            <ul>
              <li
                v-for="item in $tm(
                  `advantages_section.${User.members}.advantages_list`
                )"
                :key="$rt(item)"
              >
                <BaseIcon
                  icon="listPoint"
                  height="20"
                  width="20" />
                <span>
                  {{ $rt(item) }}
                </span>
              </li>
            </ul>
            <div class="advantages-links">
              <LocLink to="/facilities">
                <span>{{ $t("Our Fitness Network") }}</span>
                <BaseIcon
                  icon="arrow"
                  height="24"
                  width="24" />
              </LocLink>
              <BaseButton text @click="showAskQuestionModal">{{
                $t("Ask Us a Question")
              }}</BaseButton>
            </div>
          </div>
        </div>
      </div>

      <div
        class="advantages-tab advantages-tab-company"
        :class="{ 'advantages-tab_hidden': currentUser !== User.companies }"
      >
        <h3>{{ $t("For Companies") }}</h3>
        <div class="advantages-content">
          <div class="advantages-images">
            <PictureComponent
              :modern-format-image="{
                'mobile-path': '/image/advantagesSection/company-mob.webp',
                'mobile-path-retina':
                  '/image/advantagesSection/company-mob@2x.webp',
                'desktop-path': '/image/advantagesSection/company.webp',
                'desktop-path-retina':
                  '/image/advantagesSection/company@2x.webp',
              }"
              :fallback-format-image="{
                'mobile-path': '/image/advantagesSection/company-mob.png',
                'mobile-path-retina':
                  '/image/advantagesSection/company-mob@2x.png',
                'desktop-path': '/image/advantagesSection/company.png',
                'desktop-path-retina':
                  '/image/advantagesSection/company@2x.png',
              }"
              loading="lazy"
            />
          </div>
          <div class="advantages-text">
            <div class="tab-title">
              {{ $t(`advantages_section.${User.companies}.title`) }}
            </div>
            <ul>
              <li
                v-for="item in $tm(
                  `advantages_section.${User.companies}.advantages_list`
                )"
                :key="$rt(item)"
              >
                <BaseIcon
                  icon="listPoint"
                  height="20"
                  width="20" />
                <span>
                  {{ $rt(item) }}
                </span>
              </li>
            </ul>
            <div class="advantages-links">
              <LocLink to="/companies">
                <span>{{ $t("For Companies") }}</span>
                <BaseIcon
                  icon="arrow"
                  height="24"
                  width="24" />
              </LocLink>
              <BaseButton
                text
                @click="
                  showAskGetAnOfferModal(CooperationTypes.CONNECT_COMPANY)
                "
              >{{ $t("Get an Offer") }}</BaseButton
              >
            </div>
          </div>
        </div>
      </div>

      <div
        class="advantages-tab advantages-tab-partner"
        :class="{ 'advantages-tab_hidden': currentUser !== User.partners }"
      >
        <h3>{{ $t("For Partners") }}</h3>
        <div class="advantages-content">
          <div class="advantages-images">
            <PictureComponent
              :modern-format-image="{
                'mobile-path': '/image/advantagesSection/partner-mob.webp',
                'mobile-path-retina':
                  '/image/advantagesSection/partner-mob@2x.webp',
                'desktop-path': '/image/advantagesSection/partner.webp',
                'desktop-path-retina':
                  '/image/advantagesSection/partner@2x.webp',
              }"
              :fallback-format-image="{
                'mobile-path': '/image/advantagesSection/partner-mob.png',
                'mobile-path-retina':
                  '/image/advantagesSection/partner-mob@2x.png',
                'desktop-path': '/image/advantagesSection/partner.png',
                'desktop-path-retina':
                  '/image/advantagesSection/partner@2x.png',
              }"
              loading="lazy"
            />
          </div>
          <div class="advantages-text">
            <div class="tab-title">
              {{ $t(`advantages_section.${User.partners}.title`) }}
            </div>
            <ul>
              <li
                v-for="item in $tm(
                  `advantages_section.${User.partners}.advantages_list`
                )"
                :key="$rt(item)"
              >
                <BaseIcon
                  icon="listPoint"
                  height="20"
                  width="20" />
                <span>
                  {{ $rt(item) }}
                </span>
              </li>
            </ul>
            <div class="advantages-links">
              <LocLink to="/partners">
                <span>{{ $t("For Partners") }}</span>
                <BaseIcon
                  icon="arrow"
                  height="24"
                  width="24" />
              </LocLink>
              <BaseButton
                text
                @click="showAskGetAnOfferModal(CooperationTypes.PARTNER)"
              >{{ $t("Get an Offer") }}</BaseButton
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <LazyModalsAskQuestionFormModal
      v-if="mountAskQuestionModal"
      v-model:is-show-modal="isShowAskQuestionModal"
    />
  </section>
</template>

<script lang="ts">
import { CooperationTypes, User } from "~/types/entities";

import ScrollSlider, {
  IScrollSliderItem,
} from "~/components/slider/ScrollSlider.vue";
import BaseButton from "~/components/button/BaseButton.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import SelectTab from "~/components/input/SelectTab/SelectTab.vue";
import LocLink from "~/components/lang/LocLink.vue";
import { AdvantagesEvents } from "~/utils/analytics";
import { useGetAnOfferFormModal } from "~/composables/modals/getAnOfferFormModal/useGetAnOfferFormModal";

const SELECT_TAB_OPTIONS = [
  { value: "For Members", id: User.members },
  { value: "For Companies", id: User.companies },
  { value: "For Partners", id: User.partners },
];

export default defineNuxtComponent({
  components: {
    LocLink,
    SelectTab,
    ScrollSlider,
    BaseIcon,
    BaseButton,
  },
  props: {
    brand: {
      type: String,
      required: true
    }
  },
  setup() {
    const { gtag } = useGtag();
    const { showModal } = useGetAnOfferFormModal();

    const mountAskQuestionModal = ref(false);
    const isShowAskQuestionModal = ref<boolean>(false);

    const currentUser = ref<User>(SELECT_TAB_OPTIONS[0].id);
    function changeUser(user: User) {
      currentUser.value = user;
      gtag("event", AdvantagesEvents[user]);
    }
    function showAskQuestionModal() {
      !mountAskQuestionModal.value && (mountAskQuestionModal.value = true);
      isShowAskQuestionModal.value = true;
      gtag("event", AdvantagesEvents.askQuestionModal);
    }
    function showAskGetAnOfferModal(cooperationType: CooperationTypes) {
      showModal(true, cooperationType);
      gtag("event", AdvantagesEvents.getOfferModal);
    }

    return {
      mountAskQuestionModal,
      isShowAskQuestionModal,
      showModal,
      currentUser,
      changeUser,
      showAskQuestionModal,
      showAskGetAnOfferModal,
      User,
      CooperationTypes,
      SELECT_TAB_OPTIONS,
    };
  },
});
</script>
